import { useState, useMemo } from "react";
import Spinner from "../../../components/Spinners";
import Button from "../../../components/Button";
import { PopupModal } from "../../../components/Modal";
import {
  useMetasearchStatus,
  useTaxPolicies,
  useRoomRateMappings,
  useUpdateTaxPolicies,
  useUpdateRoomRateMappings,
  useOffboardHotel,
} from "../hooks";
import { FeedStatus } from "../types";
import { formatDateTime } from "../utils";

import { TaxPolicyEditor } from "./TaxPolicyEditor";
import { RoomRateMappingEditor } from "./RoomRateMappingEditor";
import { RestrictionsTable } from "./RestrictionsTable";
import { XmlPreview } from "./XmlPreview";
import { OperationsPanel } from "./OperationsPanel";
import { BookingEngineCard } from "./BookingEngineCard";
import { BrandCard } from "./BrandCard";
import { InfoRow } from "./InfoRow";

export function ConfigContent({
  configId,
  onClose,
}: {
  configId: string;
  onClose?: () => void;
}) {
  const { data: allStatus, isLoading, isError } = useMetasearchStatus();
  const { data: taxPoliciesData } = useTaxPolicies(configId);
  const { data: roomRateMappingsData } = useRoomRateMappings(configId);

  const taxPolicies = useMemo(
    () =>
      Array.isArray(taxPoliciesData?.taxPolicies)
        ? taxPoliciesData!.taxPolicies
        : Array.isArray(taxPoliciesData)
          ? taxPoliciesData
          : [],
    [taxPoliciesData],
  );

  const roomRateMappings = useMemo(
    () =>
      Array.isArray(roomRateMappingsData?.roomRateMappings)
        ? roomRateMappingsData!.roomRateMappings
        : Array.isArray(roomRateMappingsData)
          ? roomRateMappingsData
          : [],
    [roomRateMappingsData],
  );
  const updateTaxPolicies = useUpdateTaxPolicies();
  const updateRoomRateMappings = useUpdateRoomRateMappings();
  const offboardHotel = useOffboardHotel();
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);

  const config: FeedStatus | undefined = allStatus?.find(
    (s: FeedStatus) => s.configId === configId
  );

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner className="text-black" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-[#E9E9EB] p-8 text-center">
        <p className="text-red-500">Failed to load configuration</p>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-[#E9E9EB] p-8 text-center">
        <p className="text-red-500">Configuration not found</p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="bg-white rounded-lg shadow-sm border border-[#E9E9EB] p-5">
          <h3 className="text-base font-semibold text-gray-900 mb-3">
            Sync Status
          </h3>
          <InfoRow
            label="Last hotel list sync"
            value={formatDateTime(config.lastHotelListSync)}
          />
          <InfoRow
            label="Last ARI sync"
            value={formatDateTime(config.lastAriSync)}
          />
          <InfoRow
            label="Last successful sync"
            value={formatDateTime(config.lastAriSuccess)}
          />
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-[#E9E9EB] p-5">
          <h3 className="text-base font-semibold text-gray-900 mb-3">
            Configuration Summary
          </h3>
          <InfoRow
            label="Room-rate mappings"
            value={config.roomRateMappingCount}
          />
          <InfoRow label="Tax policies" value={config.taxPolicyCount} />
          <InfoRow
            label="Feed enabled"
            value={config.feedEnabled ? "Yes" : "No"}
          />
        </div>
      </div>

      <BookingEngineCard
        configId={configId}
        bookingEngineBaseUrl={config.bookingEngineBaseUrl}
        bookingEngineType={config.bookingEngineType}
        bookingEngineConfig={config.bookingEngineConfig}
      />

      <BrandCard configId={configId} currentBrandId={config.brandId} />

      <TaxPolicyEditor
        initialPolicies={taxPolicies}
        onSave={(policies) =>
          updateTaxPolicies.mutate({ configId, taxPolicies: policies })
        }
        isSaving={updateTaxPolicies.isPending}
      />

      <RoomRateMappingEditor
        initialMappings={roomRateMappings}
        onSave={(mappings) =>
          updateRoomRateMappings.mutate({
            configId,
            roomRateMappings: mappings,
          })
        }
        isSaving={updateRoomRateMappings.isPending}
      />

      <RestrictionsTable configId={configId} />

      <XmlPreview configId={configId} />

      <OperationsPanel configId={configId} />

      <div className="bg-white rounded-lg shadow-sm border border-red-200 p-5">
        <h3 className="text-base font-semibold text-red-700 mb-1">
          Danger Zone
        </h3>
        <p className="text-sm text-gray-500 mb-3">
          Remove this hotel from MetaSearch. The feed will be disabled and the
          hotel will no longer appear in Google. It can be re-onboarded later.
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowRemoveConfirm(true)}
        >
          Remove from MetaSearch
        </Button>
      </div>

      {showRemoveConfirm && (
        <PopupModal
          onClose={() => setShowRemoveConfirm(false)}
          contentClassName="mx-auto max-w-[400px] w-full"
        >
          <h3 className="text-lg font-semibold text-gray-900">
            Remove Hotel
          </h3>
          <p className="text-sm text-gray-600 mt-2">
            Are you sure you want to remove{" "}
            <strong>{config.hotelName}</strong> from MetaSearch? The feed will
            be disabled and the hotel will stop appearing in Google search
            results.
          </p>
          <div className="flex justify-end gap-2 mt-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowRemoveConfirm(false)}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={() =>
                offboardHotel.mutate(configId, {
                  onSuccess: () => {
                    setShowRemoveConfirm(false);
                    onClose?.();
                  },
                })
              }
              disabled={offboardHotel.isPending}
              loading={offboardHotel.isPending}
            >
              Remove
            </Button>
          </div>
        </PopupModal>
      )}
    </div>
  );
}
